/**
 * AE Tour — the AE's walk-through.
 * W66.X16 — in-platform guided tour.
 *
 * Role: AE (the AE)
 * Tag color: Green (#16A34A)
 */

import type { GuidedTourStep } from "./types";

export const AE_TOUR_STEPS: GuidedTourStep[] = [
  {
    stepNumber: 1,
    selector: '[data-tour="ae-what-moved-ribbon"]',
    title: "Step 1 — What moved since Friday",
    narration:
      "the AE lands here every Monday morning. The what-moved ribbon shows every account that changed state since his last session — a CP advanced, a score shifted, a hygiene flag appeared. This is not a list of everything. It is a list of what changed. Quiet accounts look quiet. That absence of signal is itself information — a deal that has not moved in seven days is a deal that needs attention today.",
    coachingBeat:
      "System alert: 2 accounts had zero movement this week. Vesuvio Holdings — last CP push was 11 days ago. That is a drop-off risk. Address it first.",
    action: "read",
    route: "/#/today",
  },
  {
    stepNumber: 2,
    selector: '[data-tour="ae-account-grid"]',
    title: "Step 2 — Highest movement account",
    narration:
      "the AE clicks into the account with the highest movement — not the highest score. A deal with the highest score but no recent movement is a stale deal that scored well months ago. Movement means something happened this week. the AE follows movement, not reputation. This is how the system teaches the AE to prioritize based on momentum rather than wishful thinking.",
    action: "click",
    route: "/#/accounts",
  },
  {
    stepNumber: 3,
    selector: '[data-tour="helix-score-viz"]',
    title: "Step 3 — The Tower (CP1-CP10 state)",
    narration:
      "Inside the account, the AE sees the Tower: ten checkpoints, CP1 through CP10, each showing its current state — green (cleared), orange (in progress), red (blocked), or gray (not yet reached). The Tower tells the AE exactly where he is in the deal and, more importantly, what is blocking it. A red CP3 means no budget clarity. A red CP6 means no signer identified. The Tower does not let the AE pretend the deal is further along than it is.",
    coachingBeat:
      "System prompt: CP5 and CP6 are both open. the AE has not identified the project sponsor or the financial signer. This deal cannot close without those two names. That is the work for this week.",
    action: "read",
    route: "/#/accounts",
  },
  {
    stepNumber: 4,
    selector: '[data-tour="helix-double-helix"]',
    title: "Step 4 — The Helix",
    narration:
      "The Helix is the visual DNA of the deal. Two strands: the qualification strand (CP1-CP4) and the execution strand (CP5-CP10). When both strands are healthy, the deal is balanced — qualified and progressing through the closing mechanics. When one strand lags the other, the AE can see it immediately. A deal that is over-qualified but under-executed means the AE is stuck at the top of the funnel. A deal that is under-qualified but over-executed means the AE is selling without confirming the basics.",
    action: "read",
    route: "/#/accounts",
  },
  {
    stepNumber: 5,
    selector: '[data-tour="mpact-composite"]',
    title: "Step 5 — M-PACT composite score and weekly delta",
    narration:
      "The M-PACT composite score is the single-number health reading for this deal: 0 to 100, calculated from all ten checkpoints weighted by deal stage. The weekly delta shows whether it went up or down since last Monday. A score of 68 with a delta of plus-4 means the deal improved. A score of 72 with a delta of minus-6 means something slipped — a CP answer changed, a flag appeared, a contact went quiet. the AE uses the delta to confirm his gut, not replace it.",
    coachingBeat:
      "System note: M-PACT score this week: 61. Delta: minus-5. The drop is driven by CP7 — Org Structure — going back to amber. the AE mapped the sponsor last month but has not updated the influence map since the procurement team changed. The system flagged the staleness.",
    action: "read",
    route: "/#/accounts",
  },
  {
    stepNumber: 6,
    selector: '[data-tour="next-steps-panel"]',
    title: "Step 6 — Suggested next step and conversation hook",
    narration:
      "The system does not just score the deal — it tells the AE what to do next. The suggested-next-step prompt is generated from the current CP state: what is open, what is blocking, what question the AE has not yet answered. With it comes a pre-written conversation hook — not a script, but a frame. the AE does not have to invent the opener. The system says: your weakest checkpoint is CP5, the authority question. Here is how to open that conversation with the sponsor.",
    coachingBeat:
      "Suggested next step: Schedule a 30-minute call with Sarah Chen (identified as project sponsor in CRM). Goal: confirm her authority level and identify who holds the financial signature. Conversation hook: 'I want to make sure we have the right people in the room before we go further. Can you help me understand how this kind of decision gets approved at your company?'",
    action: "read",
    route: "/#/accounts",
  },
  {
    stepNumber: 7,
    selector: '[data-tour="hygiene-footer"]',
    title: "Step 7 — Hygiene footer",
    narration:
      "At the bottom of the AE's account view is the hygiene footer: overdue CP answers, stale plans of record, contacts with no recent activity. These are not optional. A plan of record that is 30 days old is not a plan — it is a memory. The hygiene footer makes the AE's discipline visible. Managers see it too. A rep with four overdue CP answers is a rep whose pipeline data cannot be trusted. The system makes that visible before it becomes a forecast problem.",
    coachingBeat:
      "Hygiene alert: Plan of record last updated 34 days ago. Two CP answers are marked stale (last validated over 60 days ago). These are forecast integrity risks. Update them before the Friday review.",
    action: "read",
    route: "/#/accounts",
  },
];
